import { calculateServiceFee, parseMonetaryValue } from '@/utils';
import { DEFERA_CAPITAL_HOLD_FEE_RATE, SLICE_PAY_HOLD_FEE_RATE } from '@/utils/paymentScheduleCalculator';
import { insurancePremiumCents, withoutInsurancePremium } from '../../supabase/functions/_shared/trip-insurance.js';

/**
 * The price a trip card in the Trips list shows: the trip's plan total, with
 * the flight row that carries the price.
 *
 * The stored `grand_total_with_hold` is the source of truth (after a change of
 * flight, on Defera Zero, and with a Faye premium inside it: the card shows
 * everything the customer pays). Only when it is missing is the total rebuilt
 * from the fare (+ hotel on a package): service and hold fees are charged on
 * the TRAVEL price, never on a Faye premium, and the premium then goes on top,
 * outside every fee (migration 156), so both paths land on the same plan total.
 *
 * @param {{ trip: object[], firstFlight: object, isDeferaCapital: boolean }} p
 * @returns {object} the priced flight row with `grand_total_with_hold` set
 */
export function resolveTripListPrice({ trip, firstFlight, isDeferaCapital }) {
  const foundFlight = trip.find(f => (f.totalPrice && f.totalPrice > 0) || (f.total_amount && f.total_amount > 0) || (f.total_price && f.total_price > 0) || (f.grand_total_with_hold > 0) || (f.grand_total > 0)) || firstFlight;

  // CRITICAL: Prefer stored grand_total_with_hold when available (source of truth after change-flight, Defera Zero)
  const storedGrandTotalWithHold = parseMonetaryValue(foundFlight.grand_total_with_hold ?? foundFlight.grandTotalWithHold ?? 0);
  if (storedGrandTotalWithHold > 0) {
    return { ...foundFlight, grand_total_with_hold: storedGrandTotalWithHold };
  }

  // CRITICAL: Extract stay data from the same record providing pricing OR aggregate from trip
  const stayDataFromFlight = foundFlight.stay_data || foundFlight.stayData || null;
  const stayDataFromTrip = trip.find(f => f.stay_data || f.stayData);
  const stayData = stayDataFromFlight || (stayDataFromTrip?.stay_data || stayDataFromTrip?.stayData) || null;

  // Fees are charged on travel only: take any Faye premium out of the fare first.
  const travelFlight = withoutInsurancePremium(foundFlight);
  const premium = insurancePremiumCents(foundFlight) / 100;
  const flightPrice = parseMonetaryValue(travelFlight.totalPrice || travelFlight.total_amount || travelFlight.total_price);

  // Calculate hotel price with fallback: totalAmount OR (nightly_rate * nights)
  // Uses parseMonetaryValue to handle formatted currency strings ($1,299.00)
  let hotelPrice = 0;
  if (stayData) {
    hotelPrice = parseMonetaryValue(stayData.totalAmount || stayData.total_amount);
    // Fallback: calculate from nightly rate if totalAmount is missing
    if (hotelPrice === 0 && stayData.nightly_rate && stayData.nights) {
      hotelPrice = parseMonetaryValue(stayData.nightly_rate) * parseInt(stayData.nights, 10);
    }
  }

  const totalPrice = flightPrice + hotelPrice; // Combined total for Flights + Stays

  if (totalPrice > 0) {
    const serviceFee = calculateServiceFee(totalPrice);
    const grandTotal = totalPrice + serviceFee;
    const holdRate = isDeferaCapital ? DEFERA_CAPITAL_HOLD_FEE_RATE : SLICE_PAY_HOLD_FEE_RATE;
    const holdFee = grandTotal * holdRate;
    const grandTotalWithHold = grandTotal + holdFee;
    return { ...foundFlight, grand_total_with_hold: premium > 0 ? grandTotalWithHold + premium : grandTotalWithHold };
  }
  return { ...foundFlight, grand_total_with_hold: 0 }; // Default to 0 if no price found
}
